import { useState, useEffect, useLayoutEffect, useMemo, createContext, useContext } from "react";
import Picture from "./Picture";
import { ANALYTICS_ENABLED } from "./analytics/config";
import { openConsentSettings } from "./analytics/consent";

/* ============================================================================
   siteChrome.js — shared design system for Teorikörkort
   Exports the same Navbar / Footer / tokens used on the landing page, plus a
   LegalLayout + prose components so /privacy and /tos match exactly.
   All styling is inline JS style objects (no class-name matching to fail).
   ========================================================================== */

export const APP_STORE_URL =
  "https://apps.apple.com/se/app/teorik%C3%B6rkort/id6777672930";
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.stefkolm.korkort";
export const CONTACT_EMAIL = "support@teorikorkort.se";
export const INSTAGRAM_URL = "https://www.instagram.com/teorikorkort.se";

export const IMG = {
  logo: "/images/icon.png",
  apple: "/images/Download_on_the_App_Store_Badge.svg",
  google: "/images/google-play-badge.png",
};

export const FONT =
  '"Poppins", "Poppins Fallback", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

/* brand accent used only for legal links / numbers / accents */
export const ACCENT = "#00235e";

export const C = {
  zinc900: "#18181b", zinc700: "#3f3f46", zinc600: "#52525b", zinc500: "#71717a",
  zinc200: "#e4e4e7", zinc100: "#f4f4f5", zinc50: "#fafafa",
  gray900: "#111827", gray700: "#374151", gray600: "#4b5563", gray500: "#6b7280", gray200: "#e5e7eb",
  amber: "#fbbf24", white: "#ffffff", accent: ACCENT,
};

/* -------------------------------------------------------------------------- */
/* Hooks                                                                       */
/* -------------------------------------------------------------------------- */

function bpFor(w) {
  if (w >= 1024) return "lg";
  if (w >= 768) return "md";
  if (w >= 640) return "sm";
  return "base";
}
/* Pages are prerendered at "base" (see SSR_BP in pages/Landing.js); the first
   client render matches it and the layout effect switches before paint. */
export function useBreakpoint() {
  const [bp, setBp] = useState("base");
  useLayoutEffect(() => {
    const onResize = () => setBp(bpFor(window.innerWidth));
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return bp;
}

export function usePoppins() {
  useEffect(() => {
    const id = "tk-poppins-font";
    if (document.getElementById(id)) return;
    const pre1 = document.createElement("link");
    pre1.rel = "preconnect";
    pre1.href = "https://fonts.googleapis.com";
    const pre2 = document.createElement("link");
    pre2.rel = "preconnect";
    pre2.href = "https://fonts.gstatic.com";
    pre2.crossOrigin = "anonymous";
    const font = document.createElement("link");
    font.id = id;
    font.rel = "stylesheet";
    font.href =
      "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=optional";
    document.head.append(pre1, pre2, font);
  }, []);
}

/* -------------------------------------------------------------------------- */
/* HoverLink                                                                   */
/* -------------------------------------------------------------------------- */

export function HoverLink({ base, hover, children, ...rest }) {
  const [h, setH] = useState(false);
  return (
    <a
      {...rest}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{ ...base, ...(h ? hover : null) }}
    >
      {children}
    </a>
  );
}

/* -------------------------------------------------------------------------- */
/* Icons                                                                       */
/* -------------------------------------------------------------------------- */

const Bars3Icon = (p) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" {...p}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
  </svg>
);
const XMarkIcon = (p) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" {...p}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);
const InstagramIcon = (p) => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" {...p}>
    <path fill="currentColor" d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7zm5 3.5A4.5 4.5 0 1 1 7.5 12 4.5 4.5 0 0 1 12 7.5zm0 2A2.5 2.5 0 1 0 14.5 12 2.5 2.5 0 0 0 12 9.5zm5.25-3.75a1 1 0 1 1-1 1 1 1 0 0 1 1-1z" />
  </svg>
);

/* -------------------------------------------------------------------------- */
/* Chrome styles (nav + footer + badges)                                       */
/* -------------------------------------------------------------------------- */

function makeChrome(bp) {
  const smUp = bp === "sm" || bp === "md" || bp === "lg";
  const mdUp = bp === "md" || bp === "lg";
  const lgUp = bp === "lg";
  const badgeLinkBase = { display: "inline-block", transition: "opacity .15s cubic-bezier(.4,0,.2,1)" };
  const badgeLinkHover = { opacity: 0.8 };

  return {
    badgeLinkBase, badgeLinkHover,
    badges: {
      marginLeft: smUp ? "-.75rem" : "-.5rem", display: "flex",
      flexDirection: smUp ? "row" : "column", alignItems: smUp ? "center" : "flex-start",
      gap: smUp ? ".5rem" : ".25rem", flexWrap: smUp ? "wrap" : "nowrap", paddingTop: ".5rem",
    },
    badgeApple: { height: "56px", width: "auto" },
    badgeGoogle: { height: "82px", width: "auto", objectFit: "contain", marginBlock: "-.75rem", marginInline: "-.75rem" },

    nav: { width: "100%", padding: `1rem ${lgUp ? "3rem" : "1.5rem"}` },
    navGrid: {
      maxWidth: "1300px", marginInline: "auto", display: "grid",
      gridTemplateColumns: mdUp ? "repeat(3, minmax(0,1fr))" : "1fr auto 1fr",
      alignItems: "center", gap: "1.5rem",
    },
    navLogo: { justifySelf: "start" },
    navLogoImg: { height: "2.5rem", width: "auto" },
    navLinks: { display: mdUp ? "flex" : "none", alignItems: "center", justifySelf: "center", gap: "2rem" },
    navLink: { color: C.gray700, fontSize: ".875rem", lineHeight: 1.4286, fontWeight: 500, transition: "color .15s cubic-bezier(.4,0,.2,1)" },
    navLinkHover: { color: C.gray900 },
    navStores: { display: mdUp ? "flex" : "none", alignItems: "center", gap: ".75rem", justifySelf: "end" },
    navApple: { height: "2.5rem", width: "auto" },
    navGoogle: { height: "58px", width: "auto", marginBlock: "-.5rem" },
    burger: { display: mdUp ? "none" : "inline-flex", padding: ".5rem", justifySelf: "end", gridColumnStart: 3, border: 0, background: "transparent", cursor: "pointer" },
    burgerIcon: { width: "1.5rem", height: "1.5rem", color: C.gray900 },
    mobileMenu: { marginTop: "1rem", paddingBottom: "1rem", borderTop: `1px solid ${C.gray200}` },
    mobileInner: { display: "flex", flexDirection: "column", gap: "1rem", paddingTop: "1rem" },
    mobileLink: { color: C.gray700, fontSize: "1rem", lineHeight: 1.5, paddingInline: "1rem", transition: "color .15s cubic-bezier(.4,0,.2,1)" },
    mobileStores: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: ".75rem", paddingInline: "1rem", paddingTop: ".5rem" },

    footer: { borderTop: `1px solid ${C.gray200}`, paddingBlock: smUp ? "4rem" : "3rem" },
    footerContainer: { maxWidth: "80rem", marginInline: "auto", paddingInline: lgUp ? "3rem" : "1.5rem" },
    footerTop: { display: "grid", gap: smUp ? "3rem" : "2rem" },
    footerBrand: { display: "flex", flexDirection: "column", gap: smUp ? "1.5rem" : "1rem", alignItems: "flex-start" },
    footerLogo: { height: smUp ? "3rem" : "2.5rem", width: "auto" },
    footerCols: { display: "grid", gridTemplateColumns: mdUp ? "repeat(3, minmax(0,1fr))" : "repeat(2, minmax(0,1fr))", gap: smUp ? "2rem" : "1.5rem" },
    footerCol: { display: "flex", flexDirection: "column", gap: smUp ? "1rem" : ".75rem" },
    footerColTitle: { fontWeight: 700, color: C.gray900, fontSize: smUp ? "1rem" : ".875rem", lineHeight: 1.4286 },
    footerList: { display: "flex", flexDirection: "column", gap: ".5rem", listStyle: "none", margin: 0, padding: 0 },
    footerLink: { color: C.gray600, fontSize: smUp ? ".875rem" : ".75rem", lineHeight: 1.3333, transition: "color .15s cubic-bezier(.4,0,.2,1)" },
    footerLinkHover: { color: C.gray900 },
    footerBottom: { marginTop: "3rem", paddingTop: "2rem", borderTop: `1px solid ${C.gray200}` },
    footerCopy: { textAlign: "center", fontSize: ".875rem", lineHeight: 1.4286, color: C.gray500 },
  };
}

/* -------------------------------------------------------------------------- */
/* Navbar + Footer                                                             */
/* -------------------------------------------------------------------------- */

function StoreBadges({ s }) {
  return (
    <div style={s.badges}>
      <HoverLink href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" base={s.badgeLinkBase} hover={s.badgeLinkHover}>
        <img src={IMG.apple} alt="Ladda ned i App Store" width="120" height="40" loading="lazy" decoding="async" style={s.badgeApple} />
      </HoverLink>
      <HoverLink href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" base={s.badgeLinkBase} hover={s.badgeLinkHover}>
        <img src={IMG.google} alt="Ladda ned på Google Play" width="646" height="250" loading="lazy" decoding="async" style={s.badgeGoogle} />
      </HoverLink>
    </div>
  );
}

/* variant: "home" (same-page anchors) or "legal" (anchors point back to "/") */
export function Navbar({ variant = "home" }) {
  const bp = useBreakpoint();
  const mdUp = bp === "md" || bp === "lg";
  const s = makeChrome(bp);
  const [open, setOpen] = useState(false);
  const base = variant === "legal" ? "/sv/" : "";

  return (
    <nav style={s.nav} data-track-placement="legal_navbar">
      <div style={s.navGrid}>
        <a href="/sv/" style={s.navLogo}>
          <Picture src={IMG.logo} alt="Teorikörkort" sizes="40px" loading="eager" style={s.navLogoImg} />
        </a>

        <div style={s.navLinks}>
          <HoverLink href="/sv/" base={s.navLink} hover={s.navLinkHover}>Hem</HoverLink>
          <HoverLink href={`${base}#features`} base={s.navLink} hover={s.navLinkHover}>Funktioner</HoverLink>
          <HoverLink href={`${base}#faq`} base={s.navLink} hover={s.navLinkHover}>Vanliga frågor</HoverLink>
          <HoverLink href={`mailto:${CONTACT_EMAIL}`} base={s.navLink} hover={s.navLinkHover}>Kontakt</HoverLink>
        </div>

        <div style={s.navStores}>
          <HoverLink href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" base={s.badgeLinkBase} hover={s.badgeLinkHover}>
            <img src={IMG.apple} alt="Ladda ned i App Store" width="120" height="40" style={s.navApple} />
          </HoverLink>
          <HoverLink href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" base={s.badgeLinkBase} hover={s.badgeLinkHover}>
            <img src={IMG.google} alt="Ladda ned på Google Play" width="646" height="250" style={s.navGoogle} />
          </HoverLink>
        </div>

        <button style={s.burger} onClick={() => setOpen(!open)} aria-label="Meny" aria-expanded={open}>
          {open ? <XMarkIcon style={s.burgerIcon} /> : <Bars3Icon style={s.burgerIcon} />}
        </button>
      </div>

      {!mdUp && open && (
        <div style={s.mobileMenu}>
          <div style={s.mobileInner}>
            <HoverLink href="/sv/" base={s.mobileLink} hover={s.navLinkHover} onClick={() => setOpen(false)}>Hem</HoverLink>
            <HoverLink href={`${base}#features`} base={s.mobileLink} hover={s.navLinkHover} onClick={() => setOpen(false)}>Funktioner</HoverLink>
            <HoverLink href={`${base}#faq`} base={s.mobileLink} hover={s.navLinkHover} onClick={() => setOpen(false)}>Vanliga frågor</HoverLink>
            <HoverLink href={`mailto:${CONTACT_EMAIL}`} base={s.mobileLink} hover={s.navLinkHover} onClick={() => setOpen(false)}>Kontakt</HoverLink>
            <div style={s.mobileStores}>
              <HoverLink href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" base={s.badgeLinkBase} hover={s.badgeLinkHover}>
                <img src={IMG.apple} alt="Ladda ned i App Store" width="120" height="40" style={s.navApple} />
              </HoverLink>
              <HoverLink href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" base={s.badgeLinkBase} hover={s.badgeLinkHover}>
                <img src={IMG.google} alt="Ladda ned på Google Play" width="646" height="250" style={s.navGoogle} />
              </HoverLink>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export function Footer() {
  const bp = useBreakpoint();
  const s = makeChrome(bp);
  return (
    <footer style={s.footer} data-track-placement="legal_footer">
      <div style={s.footerContainer}>
        <div style={s.footerTop}>
          <div style={s.footerBrand}>
            <Picture src={IMG.logo} alt="Teorikörkort" sizes="48px" style={s.footerLogo} />
            <StoreBadges s={s} />
          </div>
          <div style={s.footerCols}>
            <div style={s.footerCol}>
              <h2 style={s.footerColTitle}>Juridik</h2>
              <ul style={s.footerList}>
                <li><HoverLink href="/privacy" base={s.footerLink} hover={s.footerLinkHover}>Integritetspolicy</HoverLink></li>
                <li><HoverLink href="/terms-of-service" base={s.footerLink} hover={s.footerLinkHover}>Användarvillkor</HoverLink></li>
                {ANALYTICS_ENABLED && <li><button type="button" onClick={openConsentSettings} style={{ ...s.footerLink, background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit" }}>{"Cookie-inställningar"}</button></li>}
                <li><HoverLink href="/delete-account" base={s.footerLink} hover={s.footerLinkHover}>Radera konto</HoverLink></li>
              </ul>
            </div>
            <div style={s.footerCol}>
              <h2 style={s.footerColTitle}>Företag</h2>
              <ul style={s.footerList}>
                <li><HoverLink href={`mailto:${CONTACT_EMAIL}`} base={s.footerLink} hover={s.footerLinkHover}>Kontakt</HoverLink></li>
                <li><HoverLink href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" base={s.footerLink} hover={s.footerLinkHover}>Instagram</HoverLink></li>
              </ul>
            </div>
          </div>
        </div>
        <div style={s.footerBottom}>
          <p style={s.footerCopy}>© {new Date().getFullYear()} Teorikörkort. Alla rättigheter förbehållna.</p>
        </div>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------------------- */
/* Legal prose styles                                                          */
/* -------------------------------------------------------------------------- */

function makeLegal(bp) {
  const smUp = bp === "sm" || bp === "md" || bp === "lg";
  const lgUp = bp === "lg";
  return {
    page: { minHeight: "100vh", background: C.white, fontFamily: FONT, color: C.zinc900, lineHeight: 1.5 },
    section: { paddingTop: lgUp ? "3.5rem" : "2.5rem", paddingBottom: "5rem" },
    inner: { width: "min(100% - 3rem, 46rem)", marginInline: "auto" },

    eyebrow: {
      display: "inline-block", borderRadius: "999px", background: C.zinc100,
      padding: ".25rem .75rem", fontSize: ".75rem", lineHeight: 1.3333,
      fontWeight: 600, color: C.zinc700, boxShadow: `0 0 0 1px ${C.zinc200}`,
    },
    title: {
      marginTop: "1.1rem", marginBottom: ".7rem",
      fontSize: smUp ? "3rem" : "2.25rem", lineHeight: 1.08,
      fontWeight: 800, letterSpacing: "-.03em", color: C.zinc900,
    },
    updated: {
      color: C.zinc500, fontSize: ".85rem", letterSpacing: ".02em",
      paddingBottom: "2rem", marginBottom: "2.5rem", borderBottom: `1px solid ${C.zinc200}`,
    },

    h2: { marginTop: "2.75rem", marginBottom: ".85rem", fontSize: smUp ? "1.5rem" : "1.3rem", lineHeight: 1.25, fontWeight: 800, letterSpacing: "-.02em", color: C.zinc900 },
    num: { color: C.accent, fontVariantNumeric: "tabular-nums", marginRight: ".55rem" },
    h3: { marginTop: "1.5rem", marginBottom: ".5rem", fontSize: "1.1rem", fontWeight: 700, color: C.zinc900 },
    p: { color: C.zinc600, fontSize: "1.0625rem", lineHeight: 1.75, marginBottom: "1rem" },
    ul: { color: C.zinc600, margin: "0 0 1rem", paddingLeft: "1.4rem", listStyle: "disc", fontSize: "1.0625rem", lineHeight: 1.75 },
    li: { marginBottom: ".45rem" },

    callout: { background: C.zinc50, border: `1px solid ${C.zinc200}`, borderLeft: `3px solid ${C.accent}`, borderRadius: ".9rem", padding: "1.15rem 1.3rem", margin: "1rem 0 1.5rem" },

    table: { width: "100%", borderCollapse: "collapse", margin: ".5rem 0 1.5rem", fontSize: ".92rem" },
    th: { textAlign: "left", verticalAlign: "top", padding: ".7rem .8rem", borderBottom: `1px solid ${C.zinc200}`, color: C.zinc900, fontWeight: 700 },
    td: { textAlign: "left", verticalAlign: "top", padding: ".7rem .8rem", borderBottom: `1px solid ${C.zinc200}`, color: C.zinc600 },

    contact: { marginTop: "2.5rem", padding: "1.3rem 1.5rem", background: C.zinc50, border: `1px solid ${C.zinc200}`, borderRadius: "1rem" },
    contactP: { marginBottom: ".35rem", fontSize: ".92rem", color: C.zinc700 },

    back: { display: "inline-flex", alignItems: "center", gap: ".4rem", marginTop: "2.5rem", color: C.accent, fontWeight: 700, fontSize: ".9rem", textDecoration: "none" },
  };
}

/* scoped rules only for inline text elements inside legal prose */
const LEGAL_RESET = `
.tk-page *, .tk-page *::before, .tk-page *::after { box-sizing: border-box; margin: 0; padding: 0; border: 0 solid; }
.tk-page a { color: inherit; text-decoration: none; }
.tk-page button { font: inherit; }
.tk-page img, .tk-page svg { display: block; vertical-align: middle; }
.tk-page img { max-width: 100%; }
.tk-legal strong { color: ${C.zinc900}; font-weight: 700; }
.tk-legal a { color: ${C.accent}; text-decoration: underline; text-underline-offset: 2px; }
.tk-legal a:hover { opacity: .75; }
.tk-page a:focus-visible, .tk-page button:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) { .tk-page * { transition-duration: .01ms !important; } }
`;

/* -------------------------------------------------------------------------- */
/* Legal layout + prose components                                             */
/* -------------------------------------------------------------------------- */

const LegalCtx = createContext(null);
const useS = () => useContext(LegalCtx);

export function LegalLayout({ children }) {
  usePoppins();
  const bp = useBreakpoint();
  const s = useMemo(() => makeLegal(bp), [bp]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <LegalCtx.Provider value={s}>
      <div className="tk-page" style={s.page}>
        <style>{LEGAL_RESET}</style>
        <Navbar variant="legal" />
        <main>
          <section style={s.section}>
            <div className="tk-legal" style={s.inner}>{children}</div>
          </section>
        </main>
        <Footer />
      </div>
    </LegalCtx.Provider>
  );
}

export function Eyebrow({ children }) { return <span style={useS().eyebrow}>{children}</span>; }
export function Title({ children }) { return <h1 style={useS().title}>{children}</h1>; }
export function Updated({ children }) { return <p style={useS().updated}>{children}</p>; }
export function H2({ num, children }) {
  const s = useS();
  return <h2 style={s.h2}>{num && <span style={s.num}>{num}</span>}{children}</h2>;
}
export function H3({ children }) { return <h3 style={useS().h3}>{children}</h3>; }
export function P({ children }) { return <p style={useS().p}>{children}</p>; }
export function UL({ children }) { return <ul style={useS().ul}>{children}</ul>; }
export function LI({ children }) { return <li style={useS().li}>{children}</li>; }
export function Callout({ children }) { return <div style={useS().callout}>{children}</div>; }
export function BackLink({ href = "/", children }) { return <a href={href} style={useS().back}>{children}</a>; }

export function Contact({ title, lines }) {
  const s = useS();
  return (
    <div style={s.contact}>
      <p style={{ ...s.contactP, fontWeight: 700, color: C.zinc900 }}>{title}</p>
      {lines.map((ln, i) => <p key={i} style={s.contactP}>{ln}</p>)}
    </div>
  );
}

export function LegalTable({ head, rows }) {
  const s = useS();
  return (
    <table style={s.table}>
      <thead>
        <tr>{head.map((h, i) => <th key={i} style={s.th}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri}>
            {r.map((c, ci) => (
              <td key={ci} style={ri === rows.length - 1 ? { ...s.td, borderBottom: "none" } : s.td}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}